import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, CalendarDays, ChevronDown } from 'lucide-react';
import { NAV_LINKS as FALLBACK_NAV_LINKS } from '../mock';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { LogoIcon } from './Logo';

const AnnouncementBar = () => {
  const [msgs, setMsgs] = useState([
    'Free shipping on orders over ₹ 10,000',
    'IGI Certified Lab Grown Diamonds',
    'Lifetime Exchange & Buyback',
  ]);
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % msgs.length), 4000);
    return () => clearInterval(id);
  }, [msgs.length]);

  return (
    <div className="bg-[var(--brand-green)] text-[var(--brand-green-foreground)] text-[11px] tracking-widest-2 uppercase py-2.5 text-center">
      <span className="inline-block px-4">{msgs[i]}</span>
    </div>
  );
};

// Tailwind's `lg` breakpoint (1024px) — the hero's transparent-header
// overlay is a mobile-only layout (see HeroSlider's `-mt-20 lg:mt-0`), so
// this must match that breakpoint exactly or the header can go transparent
// with nothing but blank white page behind it on desktop.
const LG_BREAKPOINT = 1024;

const Header = () => {
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [hoverLink, setHoverLink] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < LG_BREAKPOINT : true);
  const [openMobileCat, setOpenMobileCat] = useState(null);
  const navLinks = FALLBACK_NAV_LINKS;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    const onResize = () => setIsMobile(window.innerWidth < LG_BREAKPOINT);
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // The transparent-over-hero overlay only applies on mobile, where the hero
  // is pulled up under the header (see HeroSlider). On desktop the header is
  // always solid white.
  const isHome = location.pathname === '/';
  const transparentOverHero = isHome && !scrolled && isMobile;
  const textColor = transparentOverHero ? 'text-white' : 'text-[#1a1a1a]';

  return (
    <div className="sticky top-0 z-40">
      {/* <AnnouncementBar /> */}
      <header
        className={`transition-colors duration-300 ${transparentOverHero ? 'bg-transparent' : 'bg-white'} ${scrolled ? 'shadow-[0_1px_0_rgba(0,0,0,0.10)]' : ''}`}
        onMouseLeave={() => setHoverLink(null)}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-20 lg:h-20 flex items-center justify-between">
          {/* Left — mobile menu + appointments + reviews */}
          <div className="flex items-center gap-6 flex-1">
            <button className={`lg:hidden p-2 -m-2 ${textColor}`} onClick={() => setMobileOpen(true)} aria-label="Menu"><Menu size={20} /></button>
            <Link to="/appointments" className={`hidden lg:flex items-center gap-2 text-[11px] tracking-widest-2 uppercase ${textColor}`}>
              <CalendarDays size={14} strokeWidth={1.4} /> <span className="hover-underline">Appointments</span>
            </Link>
            <Link to="/reviews" className={`hidden lg:block text-[11px] tracking-widest-2 uppercase ${textColor}`}>
              <span className="hover-underline">Reviews</span>
            </Link>
          </div>

          {/* Center — wordmark only */}
          <Link to="/" className="flex-shrink-0">
            <div className="flex flex-col items-center leading-none">
              <div className={`font-script text-[30px] lg:text-[36px] leading-none tracking-[0.02em] ${transparentOverHero ? 'text-[#FBFEE3]' : 'text-[#1a1a1a]'}`}>House of Seya</div>
            </div>
          </Link>

          {/* Right — home + about + cart */}
          <div className="flex items-center gap-5 flex-1 justify-end">
            <Link to="/" className={`hidden sm:block text-[11px] tracking-widest-2 uppercase ${textColor}`}>
              <span className="hover-underline">Home</span>
            </Link>
            <Link to="/about" className={`hidden sm:block text-[11px] tracking-widest-2 uppercase ${textColor}`}>
              <span className="hover-underline">About</span>
            </Link>
            <button aria-label="Cart" onClick={() => setIsOpen(true)} className={`relative p-2 -m-2 ${textColor}`}>
              <ShoppingBag size={18} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute top-0 right-0 bg-[var(--brand-sage)] text-[#FFFFFF] text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
              )}
            </button>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className={`hidden lg:block border-t ${transparentOverHero ? 'border-[rgba(255,255,255,0.25)]' : 'border-[#e5ddce]'}`}>
          <div className="max-w-[1600px] mx-auto px-10">
            <ul className="flex justify-center gap-10 xl:gap-14">
              {navLinks.map(link => (
                <li
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setHoverLink(link.label)}
                >
                  <Link
                    to={link.href}
                    className={`block py-4 text-[11px] tracking-widest-2 uppercase ${textColor} hover:text-[#999959] transition-colors`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Mega menu */}
          {hoverLink && navLinks.find(l => l.label === hoverLink)?.sub && (
            <div
              className="absolute left-0 right-0 top-full bg-[#FFFFFF] border-t border-[#e5ddce] shadow-lg"
              onMouseEnter={() => setHoverLink(hoverLink)}
            >
              <div className="max-w-[1600px] mx-auto px-10 py-8">
                <div className="grid grid-cols-4 gap-8">
                  <div>
                    <div className="font-serif text-sm text-[#7a6a54] mb-3">{hoverLink}</div>
                    <ul className="space-y-2">
                      {navLinks.find(l => l.label === hoverLink).sub.map(s => (
                        <li key={s.href}>
                          <Link to={s.href} className="text-[13px] text-[#1a1a1a] hover:text-[#999959] hover-underline">{s.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="bg-[#FFFFFF] w-screen max-w-none flex flex-col overflow-y-auto">
            <SheetHeader className="sr-only">
              <SheetTitle>House of Seya Menu</SheetTitle>
              <SheetDescription>Main navigation menu for House of Seya</SheetDescription>
            </SheetHeader>
            <button
              onClick={() => { setMobileOpen(false); navigate('/'); }}
              aria-label="Home"
              className="mb-8"
            >
              <LogoIcon size={40} black />
            </button>

            {/* EARRINGS / NECKLACES / BRACELETS */}
            <ul className="space-y-3">
              {navLinks.filter(l => ['Earrings', 'Necklaces', 'Bracelets'].includes(l.label)).map(l => (
                <li key={l.label}>
                  <button
                    onClick={() => setOpenMobileCat(openMobileCat === l.label ? null : l.label)}
                    aria-expanded={openMobileCat === l.label}
                    className="w-full flex items-center justify-between gap-3"
                  >
                    <span className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a]">{l.label}</span>
                    {l.sub?.length > 0 && (
                      <ChevronDown size={18} strokeWidth={1.6} className={`text-[#7a6a54] transition-transform flex-shrink-0 ${openMobileCat === l.label ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {l.sub?.length > 0 && openMobileCat === l.label && (
                    <ul className="mt-3 ml-3 space-y-3 border-l border-[#e5ddce] pl-4">
                      {l.sub.map(s => (
                        <li key={s.href}>
                          <button
                            onClick={() => { setMobileOpen(false); navigate(s.href); }}
                            className="text-[11px] text-[#5a5346] tracking-wide uppercase"
                          >{s.label}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {/* Services / Guide divider */}
            <div className="border-t border-[#e5ddce] my-3" />

            {/* SERVICES / JEWELLERY GUIDE */}
            <ul className="space-y-3">
              {navLinks.filter(l => ['Services', 'Jewellery Guide'].includes(l.label)).map(l => (
                <li key={l.label}>
                  <button
                    onClick={() => setOpenMobileCat(openMobileCat === l.label ? null : l.label)}
                    aria-expanded={openMobileCat === l.label}
                    className="w-full flex items-center justify-between gap-3"
                  >
                    <span className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a]">{l.label}</span>
                    {l.sub?.length > 0 && (
                      <ChevronDown size={18} strokeWidth={1.6} className={`text-[#7a6a54] transition-transform flex-shrink-0 ${openMobileCat === l.label ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {l.sub?.length > 0 && openMobileCat === l.label && (
                    <ul className="mt-3 ml-3 space-y-3 border-l border-[#e5ddce] pl-4">
                      {l.sub.map(s => (
                        <li key={s.href}>
                          <button
                            onClick={() => { setMobileOpen(false); navigate(s.href); }}
                            className="text-[11px] text-[#5a5346] tracking-wide uppercase"
                          >{s.label}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {/* About / Reviews divider */}
            <div className="border-t border-[#e5ddce] my-3" />

            {/* ABOUT / REVIEWS */}
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => { setMobileOpen(false); navigate('/about'); }}
                  className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a]"
                >About</button>
              </li>
              <li>
                <button
                  onClick={() => { setMobileOpen(false); navigate('/reviews'); }}
                  className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a]"
                >Reviews</button>
              </li>
            </ul>
          </SheetContent>
        </Sheet>
      </header>
    </div>
  );
};

export default Header;
